import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Loader, Send, Calendar, User, Mail, Phone, Utensils } from "lucide-react";
import { cn } from "@/lib/utils";
function InquiryFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
                    <Send className="w-10 h-10 text-primary" />
                </div>
                <div className="space-y-2">
                    <h3 className="text-2xl font-display font-semibold text-foreground">Inquiry Received!</h3>
                    <p className="text-muted-foreground max-w-md mx-auto">{successMessage}</p>
                </div>
                <Button onClick={resetForm} variant="outline" className="mt-4 rounded-md">
                    Send Another Request
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {}
                <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel>Your Name</FormLabel>
                            <FormControl>
                                <div className="relative">
                                    <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                                    <Input placeholder="John Doe" className="pl-9 rounded-md bg-white/50 focus:bg-white transition-colors" {...field} />
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                {}
                <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel>Email Address</FormLabel>
                            <FormControl>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                                    <Input type="email" placeholder="john@example.com" className="pl-9 rounded-md bg-white/50 focus:bg-white transition-colors" {...field} />
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {}
                <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
                            <FormLabel>Phone Number <span className="text-muted-foreground text-xs font-normal ml-1">(Optional)</span></FormLabel>
                            <FormControl>
                                <div className="relative">
                                    <Phone className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                                    <Input type="tel" placeholder="+49 123 4567890" className="pl-9 rounded-md bg-white/50 focus:bg-white transition-colors" {...field} />
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                {}
                <FormField control={control} name="event_date" render={({
        field
      }) => <FormItem>
                            <FormLabel>Preferred Event Date <span className="text-muted-foreground text-xs font-normal ml-1">(Optional)</span></FormLabel>
                            <FormControl>
                                <div className="relative">
                                    <SafeHydrate><Calendar className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" /></SafeHydrate>
                                    <Input type="date" className="pl-9 rounded-md bg-white/50 focus:bg-white transition-colors" {...field} />
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>

            {}
            <FormField control={control} name="event_type" rules={{
      required: "Please select an event type"
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel>Event Type</FormLabel>
                        <FormControl>
                            <div className="relative">
                                <Utensils className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                                <select className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-white/50 px-3 pl-9 py-1 text-base shadow-xs transition-colors outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus:bg-white placeholder:text-muted-foreground" {...field}>
                                    <option value="" disabled>Select an event type</option>
                                    {["Private Party", "Business Event", "Wedding", "Other"].map((opt, i) => <option key={opt} value={opt} data-index={i}>{opt}</option>)}
                                </select>
                            </div>
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            {}
            <FormField control={control} name="message" rules={{
      maxLength: {
        value: 1000,
        message: "Message too long (max 1000 characters)"
      }
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel>Additional Details / Message</FormLabel>
                        <FormControl>
                            <Textarea placeholder="Tell us more about your event, menu preferences, etc." className="min-h-[120px] resize-y rounded-md bg-white/50 focus:bg-white transition-colors" {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            {}
            {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm flex items-center gap-2">
                    <span className="font-semibold">Error:</span> {submitError}
                </div>}

            {}
            <Button type="submit" className="w-full md:w-auto md:min-w-[200px] rounded-md font-semibold text-base py-6 shadow-soft hover:shadow-medium transition-all" disabled={isSubmitting}>
                {isSubmitting ? <>
                        <Loader className="mr-2 h-5 w-5 animate-spin" />
                        Sending Inquiry...
                    </> : "Send Inquiry"}
            </Button>
        </Form>;
}
export default function BookingInquirySection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return;
}